import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface MicrobeType {
  name: string;
  nameEn: string;
  icon: string;
  count: string;
  location: string;
  function: string;
  color: string;
  percentage: number;
}

const MICROBES: MicrobeType[] = [
  {
    name: '拟杆菌门',
    nameEn: 'Bacteroidetes',
    icon: '🦠',
    count: '~30%',
    location: '大肠',
    function: '分解复杂碳水化合物，产生短链脂肪酸',
    color: '#4ECDC4',
    percentage: 30,
  },
  {
    name: '厚壁菌门',
    nameEn: 'Firmicutes',
    icon: '🔬',
    count: '~50%',
    location: '全消化道',
    function: '发酵产能，合成维生素K和B族维生素',
    color: '#667EEA',
    percentage: 50,
  },
  {
    name: '变形菌门',
    nameEn: 'Proteobacteria',
    icon: '💫',
    count: '~10%',
    location: '小肠',
    function: '包含大肠杆菌等，参与氮循环代谢',
    color: '#FF6B35',
    percentage: 10,
  },
  {
    name: '放线菌门',
    nameEn: 'Actinobacteria',
    icon: '🌟',
    count: '~5%',
    location: '肠道表层',
    function: '包含双歧杆菌，维护肠道屏障功能',
    color: '#FFD700',
    percentage: 5,
  },
];

const FACTS = [
  { num: '38万亿', label: '人体微生物数量', color: '#4ECDC4' },
  { num: '1000+', label: '肠道菌种', color: '#667EEA' },
  { num: '1.5kg', label: '肠道微生物总重', color: '#FF6B35' },
  { num: '70%', label: '免疫细胞在肠道', color: '#FFD700' },
];

const HEALTH_IMPACTS = [
  { title: '消化吸收', desc: '分解食物纤维，合成必需维生素', icon: '🍎' },
  { title: '免疫调节', desc: '训练免疫系统，抵抗病原菌', icon: '🛡️' },
  { title: '脑肠轴', desc: '通过迷走神经影响情绪和认知', icon: '🧠' },
  { title: '代谢平衡', desc: '调节血糖、脂肪储存和能量代谢', icon: '⚖️' },
];

const Microbe: React.FC<{
  x: number;
  y: number;
  size: number;
  color: string;
  frame: number;
  seed: number;
}> = ({ x, y, size, color, frame, seed }) => {
  const wobble = Math.sin(frame * 0.06 + seed) * 8;
  const scale = 1 + Math.sin(frame * 0.08 + seed * 2) * 0.15;

  return (
    <div
      style={{
        position: 'absolute',
        left: x + wobble,
        top: y + Math.cos(frame * 0.04 + seed) * 6,
        width: size,
        height: size * 0.7,
        borderRadius: '50%',
        background: `radial-gradient(ellipse at 35% 35%, ${color}88, ${color}33)`,
        border: `1px solid ${color}44`,
        transform: `scale(${scale}) rotate(${wobble * 2}deg)`,
      }}
    />
  );
};

export const MicrobiomeAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  const floatingMicrobes = React.useMemo(() => {
    const out: { x: number; y: number; s: number; c: string; seed: number }[] = [];
    for (let i = 0; i < 30; i++) {
      const h1 = ((i * 7919 + 104729) % 100000) / 100000;
      const h2 = ((i * 6271 + 73849) % 100000) / 100000;
      out.push({
        x: h1 * 1920,
        y: h2 * 1080,
        s: 10 + h1 * 25,
        c: MICROBES[i % MICROBES.length].color,
        seed: i * 1.7,
      });
    }
    return out;
  }, []);

  return (
    <AbsoluteFill
      style={{
        background: 'radial-gradient(ellipse at 50% 50%, #0a1628 0%, #060d1a 60%, #030810 100%)',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      {floatingMicrobes.map((m, i) => (
        <Microbe key={i} x={m.x} y={m.y} size={m.s} color={m.c} frame={frame} seed={m.seed} />
      ))}

      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 60,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #4ECDC4, #667EEA)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          人体微生物组
        </h1>
        <p style={{ fontSize: 30, margin: '6px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          Human Microbiome · 体内的隐形生态系统
        </p>
      </div>

      {/* Stats row */}
      <div
        style={{
          position: 'absolute',
          top: 220,
          left: 60,
          right: 60,
          display: 'flex',
          gap: 20,
        }}
      >
        {FACTS.map((fact, i) => {
          const fProg = spring({
            frame: frame - 15 - i * 8,
            fps,
            config: { damping: 14, stiffness: 120 },
          });

          return (
            <div
              key={fact.label}
              style={{
                flex: 1,
                padding: '16px 20px',
                background: `${fact.color}08`,
                border: `1px solid ${fact.color}25`,
                borderRadius: 16,
                textAlign: 'center',
                opacity: fProg,
                transform: `scale(${fProg})`,
              }}
            >
              <div style={{ fontSize: 38, fontWeight: 800, color: fact.color }}>
                {fact.num}
              </div>
              <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                {fact.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Microbe detail cards — 2×2 grid */}
      <div
        style={{
          position: 'absolute',
          top: 380,
          left: 60,
          right: 60,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 20,
        }}
      >
        {MICROBES.map((microbe, i) => {
          const enterDelay = 55 + i * 15;
          const cardProg = spring({
            frame: frame - enterDelay,
            fps,
            config: { damping: 14, stiffness: 120 },
          });

          const barProg = interpolate(frame, [enterDelay + 20, enterDelay + 50], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });

          return (
            <div
              key={microbe.nameEn}
              style={{
                width: 'calc(50% - 10px)',
                padding: '24px 28px',
                background: `${microbe.color}08`,
                border: `2px solid ${microbe.color}30`,
                borderRadius: 20,
                opacity: cardProg,
                transform: `translateY(${interpolate(cardProg, [0, 1], [20, 0])}px)`,
                boxSizing: 'border-box',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 46 }}>{microbe.icon}</span>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: 34, fontWeight: 700, color: microbe.color, margin: 0 }}>
                    {microbe.name}
                  </h3>
                  <span style={{ fontSize: 28, color: 'rgba(255,255,255,0.35)' }}>
                    {microbe.nameEn}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 36,
                    fontWeight: 800,
                    color: microbe.color,
                  }}
                >
                  {microbe.count}
                </div>
              </div>

              <p style={{ fontSize: 26, color: '#D1D5DB', lineHeight: 1.5, margin: '0 0 14px' }}>
                {microbe.function}
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 26, color: 'rgba(255,255,255,0.35)' }}>
                  📍 {microbe.location}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 14,
                    background: 'rgba(255,255,255,0.06)',
                    borderRadius: 5,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${microbe.percentage * barProg}%`,
                      height: '100%',
                      background: `linear-gradient(90deg, ${microbe.color}, ${microbe.color}66)`,
                      borderRadius: 5,
                    }}
                  />
                </div>
                <span style={{ fontSize: 26, color: microbe.color, fontWeight: 600 }}>
                  {microbe.percentage}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Health impacts */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 60,
          right: 60,
          display: 'flex',
          gap: 14,
        }}
      >
        {HEALTH_IMPACTS.map((h, i) => {
          const hProg = spring({
            frame: frame - 140 - i * 8,
            fps,
            config: { damping: 14, stiffness: 120 },
          });

          return (
            <div
              key={h.title}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 12,
                opacity: hProg,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span style={{ fontSize: 30 }}>{h.icon}</span>
              <div>
                <div style={{ fontSize: 26, fontWeight: 600, color: '#4ECDC4' }}>{h.title}</div>
                <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.35)' }}>{h.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
